import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Settings,
  LogOut,
  Menu,
  X,
  FileText,
  ChevronDown,
  Plus,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { CreateTeamModal } from '@/components/modals/CreateTeamModal';
import { teamsApi } from '@/lib/api';
import { getInitials } from '@/lib/utils';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/reports', icon: FileText, label: 'Reports' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const { user, logout, checkAuth, isLoading } = useAuthStore();
  const { currentTeam, teams, setCurrentTeam, fetchTeams } = useTeamStore();
  const navigate = useNavigate();

  // Fetch teams on mount
  const { data: teamsData, refetch: refetchTeams } = useQuery({
    queryKey: ['teams'],
    queryFn: teamsApi.list,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Sync fetched teams with team store
  useEffect(() => {
    if (teamsData?.teams) {
      fetchTeams();
    }
  }, [teamsData, fetchTeams]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleTeamSwitch = (teamId: string) => {
    setCurrentTeam(teamId);
    // Optionally refresh data when switching teams
    window.location.reload();
  };

  const handleTeamCreated = () => {
    refetchTeams();
    fetchTeams();
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform border-r border-gray-200 bg-white transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
            <h1 className="text-primary-600 text-xl font-bold">PianoProjects</h1>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Team Selector */}
          <div className="border-b border-gray-200 px-3 py-3">
            <DropdownMenu
              align="left"
              trigger={
                <button className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm transition-colors hover:bg-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary-100 text-primary-700 flex h-6 w-6 items-center justify-center rounded text-xs font-medium">
                      {currentTeam?.name?.charAt(0)?.toUpperCase() || 'T'}
                    </div>
                    <span className="max-w-[120px] truncate font-medium text-gray-900">
                      {currentTeam?.name || 'Select Team'}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
              }
            >
              {teams.map((team) => (
                <DropdownMenuItem
                  key={team.id}
                  onClick={() => handleTeamSwitch(team.id)}
                  className={cn(currentTeam?.id === team.id && 'bg-gray-100')}
                >
                  <div className="bg-primary-100 text-primary-700 mr-2 flex h-5 w-5 items-center justify-center rounded text-xs font-medium">
                    {team.name.charAt(0).toUpperCase()}
                  </div>
                  {team.name}
                </DropdownMenuItem>
              ))}
              {teams.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => setShowCreateTeamModal(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create New Team
              </DropdownMenuItem>
            </DropdownMenu>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                  )
                }
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="h-5 w-5" />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatarUrl} />
                <AvatarFallback>{user ? getInitials(user.name) : '?'}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="truncate text-xs text-gray-500">{user?.email}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center border-b border-gray-200 bg-white px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Create Team Modal */}
      <CreateTeamModal
        isOpen={showCreateTeamModal}
        onClose={() => setShowCreateTeamModal(false)}
        onSuccess={handleTeamCreated}
      />
    </div>
  );
}
